import React, { ReactElement, useContext } from 'react'
import PersonLink from '../Person/PersonLink'
import { AppContext } from '../contexts/AppContext'
import { Entry } from './Entry.d'
import { ENTRY_TYPE_NORMAL } from '../Write/config'
import { formatPage, outputTime } from '../helpers/format'
import { EntryContext } from '../contexts/EntryContext'

// Note that this must contain format.js:regexTime.
const regexTimeInText = /(\n|^)\s*-?\s*(~?)([0-9]{2,})[: ]?([0-5][0-9])(~?)(:| -)? /g

/* Format times of day consistently throughout summary and entry */
const replaceTimesOfDay = (fullText: string, start: number, end: number | undefined, keyStart: number): (ReactElement | string)[] => {
    const text = fullText.substring(start, end)

    const segments: (ReactElement | string)[] = []
    let startPos = 0

    for (const match of text.matchAll(regexTimeInText)) {
        // Matches have to be at the start of their respective line.
        if (keyStart !== 0 && fullText.substr(start + (match.index || 0), 1) !== '\n') {
            continue
        }

        const time = outputTime(match[0], true)

        segments.push(text.substring(startPos, match.index || 0))
        if (match[0][0] === '\n') {
            segments.push('\n')
        }
        segments.push(
            <span key={ keyStart + segments.length + 1 }>
                &bull;&nbsp;{time}:&nbsp;
            </span>)
        startPos = (match.index || 0) + match[0].length
    }
    const tail = text.substring(startPos)
    if (tail) {
        segments.push(tail)
    }

    return segments
}

/* Replace names with <PersonLink>s pointing to their /person/ page */
const replaceNamesAndTimes = (text: string, matches: string, summaryLength: number): (ReactElement | string)[] => {
    const segments: (ReactElement | string)[] = []
    let startPos = 0
    for (const match of matches.split('; ')) {
        if (!match) {
            continue
        }
        const entryParts = match.split('@')
        const positions = entryParts[1].split('-')
        const start = parseInt(positions[0]) - summaryLength
        const end = parseInt(positions[1]) - summaryLength
        if (start < 0 || start > text.length) {
            continue
        }
        const person = <PersonLink
            key={ segments.length }
            text={ text.substring(start - 1, end) }
            personId={ entryParts[0] } />
        segments.push(...replaceTimesOfDay(text, startPos, start - 1, segments.length))
        segments.push(person)
        startPos = end
    }
    segments.push(...replaceTimesOfDay(text, startPos, undefined, segments.length))
    return segments
}

// Mirror of R's grammar (r/config/global.config.r); its POSIX classes match any-script letters under C.UTF-8, so \p + the u flag.
const RE_NAME_PART = '\\p{Lu}(?:[\\p{Ll}\'][\\p{L}]{0,20})?\\p{Ll}'
const RE_NAME = `${RE_NAME_PART}(?:-${RE_NAME_PART})*`
const RE_COUNTRY = '[A-Z]{2,3}'
const RE_SURNAME = ` +\\((?:(?:v[ao]n|de[rns]?|l[ea]s?|y|${RE_NAME})[ \\-']?){1,5}\\)`
// Group 1 = name as written (with any country code), group 2 = optional surname; only the -0 tag is dropped.
const ignoredNamePattern = new RegExp(`(${RE_NAME}(?:${RE_COUNTRY})?)-0\\b(${RE_SURNAME})?`, 'gu')

const markIgnoredNames = (text: string, keyBase: number): (ReactElement | string)[] => {
    const parts: (ReactElement | string)[] = []
    let last = 0
    for (const match of text.matchAll(ignoredNamePattern)) {
        const at = match.index || 0
        parts.push(text.substring(last, at))
        parts.push(<span key={ `ignored-${keyBase}-${at}` } className="ignored-name">{ match[1] + (match[2] || '') }</span>)
        last = at + match[0].length
    }
    parts.push(text.substring(last))
    return parts
}

const MAX_ENTRY_PREVIEW = 100
const MATCHED_STATUSES = ['processed', 'deleted']

const EntryContentText = (props: { entry: Entry, showSummary?: boolean }): ReactElement => {
    const { hasLoginScope } = useContext(AppContext)
    const { state } = useContext(EntryContext)

    const rawText = (!props.showSummary
        ? props.entry.entry
        : props.entry.summary
            ? props.entry.summary
            : (!props.entry.entry || props.entry.entry.length <= MAX_ENTRY_PREVIEW)
                ? props.entry.entry
                : props.entry.entry.substring(0, MAX_ENTRY_PREVIEW - 2) + '…')
              || ''

    // Name-match offsets are relative to the untrimmed text, so the trimmed leading length is added back below.
    const leadingTrimmed = rawText.length - rawText.trimStart().length
    const text = rawText.trim()

    if (!text && !props.entry.summary && !props.entry.title) {
        return hasLoginScope('write')
            ? <div className="tip">This entry is empty.</div>
            : <></>
    }

    const summaryLength = !props.showSummary && props.entry.summary
        ? props.entry.summary.length + 1
        : 0

    const segments = MATCHED_STATUSES.includes(props.entry.status || '') && !(state.write && props.entry.id in state.write)
        ? replaceNamesAndTimes(text, props.entry.matches || '', summaryLength + leadingTrimmed)
        : text

    const output = typeof segments === 'string'
        ? markIgnoredNames(segments, 0)
        : segments.flatMap((seg, i) => typeof seg === 'string' ? markIgnoredNames(seg, i) : [seg])

    return <>
        { props.showSummary && <h3 className="tip">{output}</h3>}
        { props.entry.type !== ENTRY_TYPE_NORMAL && props.entry.endText && !hasLoginScope('write')
          && <div className="tip">Pages { formatPage(props.entry.date) } to { formatPage(props.entry.date, props.entry.endText) }</div> }
        { !props.showSummary && output }
    </>
}

export default EntryContentText
